import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Input, Checkbox } from '../components/common';
import { useAuthStore } from '../store/authStore';
import { getRememberMe } from '../utils/secureStorage';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

export const LoginScreen: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const { login, isLoading, error, clearError } = useAuthStore();

  // 保存された「ログイン情報を記憶する」設定を読み込む
  useEffect(() => {
    const loadRememberMe = async () => {
      const savedRememberMe = await getRememberMe();
      setRememberMe(savedRememberMe);
    };
    loadRememberMe();
  }, []);

  const handleLogin = async () => {
    if (!identifier.trim() || !password.trim()) {
      Alert.alert('エラー', 'ハンドルとパスワードを入力してください');
      return;
    }

    try {
      clearError();
      await login(identifier.trim(), password, rememberMe);
    } catch (err) {
      Alert.alert(
        'ログインエラー',
        error || 'ログインに失敗しました。ハンドルとパスワードを確認してください。'
      );
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.header}>
            <Text style={styles.title}>ClipSky</Text>
            <Text style={styles.subtitle}>Blueskyへログイン</Text>
          </View>

          <View style={styles.form}>
            <Input
              label="ハンドルまたはメールアドレス"
              placeholder="username.bsky.social"
              value={identifier}
              onChangeText={setIdentifier}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              leftIcon="at"
              editable={!isLoading}
            />

            <Input
              label="パスワード"
              placeholder="パスワードを入力"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
              autoCorrect={false}
              leftIcon="lock-closed"
              rightIcon={showPassword ? 'eye-off' : 'eye'}
              onRightIconPress={() => setShowPassword(!showPassword)}
              editable={!isLoading}
            />

            <View style={styles.checkboxContainer}>
              <Checkbox
                label="ログイン情報を記憶する"
                checked={rememberMe}
                onChange={setRememberMe}
                disabled={isLoading}
              />
            </View>

            <Button
              title={isLoading ? 'ログイン中...' : 'ログイン'}
              onPress={handleLogin}
              loading={isLoading}
              disabled={isLoading}
              fullWidth
              size="large"
              style={styles.loginButton}
            />

            <Text style={styles.helpText}>
              Blueskyアカウントをお持ちでない場合は、{'\n'}
              bsky.appから新規登録してください
            </Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing['2xl'],
  },
  title: {
    ...typography.h1,
    color: colors.primary,
    marginBottom: spacing.sm,
  },
  subtitle: {
    ...typography.bodyLarge,
    color: colors.textSecondary,
  },
  form: {
    width: '100%',
  },
  checkboxContainer: {
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  loginButton: {
    marginTop: spacing.md,
    marginBottom: spacing.lg,
  },
  helpText: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
});
